import React, { useState, useEffect } from 'react';
import api from '../../utils/axiosConfig';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/ListPage.css';

function MeasureSheetList() {
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(true);
    const [sheets, setSheets] = useState([]);
    const [selectedSheets, setSelectedSheets] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredSheets, setFilteredSheets] = useState([]);

    const refreshSheets = async () => {
        const res = await api.get('/measuresheets');
        const sortedSheets = res.data.sort((a, b) => new Date(b.sheetDate) - new Date(a.sheetDate));
        setSheets(sortedSheets);
        setFilteredSheets(sortedSheets);
    };

    useEffect(() => {
        withLoading(refreshSheets).catch(() => alert('Error fetching Measure Sheets'));
    }, [withLoading]);

    useEffect(() => {
        const results = sheets.filter(sheet =>
            sheet.partyName.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredSheets(results);
    }, [searchTerm, sheets]);

    const handleCheckboxChange = (sheetId) => {
        setSelectedSheets(prev =>
            prev.includes(sheetId) ? prev.filter(id => id !== sheetId) : [...prev, sheetId]
        );
    };

    const handleSelectAll = (e) => {
        setSelectedSheets(e.target.checked ? filteredSheets.map(sheet => sheet.id) : []);
    };

    const handleDeleteSelected = () => {
        if (!window.confirm('Are you sure you want to delete the selected sheets?')) return;
        Promise.all(selectedSheets.map(sheetId => api.delete(`/measuresheets/${sheetId}`)))
            .then(() => {
                setSheets(prev => prev.filter(sheet => !selectedSheets.includes(sheet.id)));
                setSelectedSheets([]);
            })
            .catch(() => alert('Error deleting Measure Sheet'));
    };

    const handleDuplicate = async (sheet) => {
        try {
            const response = await api.get(`/measuresheets/${sheet.id}`);
            const completeSheet = response.data;
            const duplicateData = {
                partyName: `${completeSheet.partyName} (Duplicate)`,
                sheetDate: new Date().toISOString().split('T')[0],
                groups: completeSheet.groups.map(group => ({
                    title: group.title,
                    total: group.total,
                    items: group.items.map(item => ({
                        description: item.description,
                        nose: item.nose,
                        length: item.length,
                        width: item.width,
                        sqft: item.sqft,
                        isSubtraction: item.isSubtraction
                    }))
                }))
            };
            await api.post('/measuresheets', duplicateData);
            await refreshSheets();
        } catch (error) {
            console.error('Error duplicating sheet:', error);
            alert('Failed to duplicate measure sheet');
        }
    };

    const allSelected = filteredSheets.length > 0 && selectedSheets.length === filteredSheets.length;

    return (
        <PageLoadingArea isLoading={isLoading}>
        <div className="list-page">
            <div className="list-page-header">
                <div className="list-page-title">
                    <h1>Measure Sheets</h1>
                    <p className="list-page-subtitle">
                        {filteredSheets.length} sheet{filteredSheets.length === 1 ? '' : 's'}
                    </p>
                </div>
                <div className="list-header-actions">
                    <div className="list-search-wrap">
                        <input
                            type="text"
                            placeholder="Search by party name"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="list-search-input"
                        />
                    </div>
                    <div className="list-button-group">
                        <button type="button" onClick={() => navigate('/createMeasureSheet')} className="list-add-button">
                            Add New Sheet
                        </button>
                        {selectedSheets.length > 0 && (
                            <button type="button" onClick={handleDeleteSelected} className="list-delete-button">
                                Delete ({selectedSheets.length})
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="list-table-card">
                <div className="list-table-scroll">
                    <table className="list-table">
                        <thead>
                            <tr>
                                <th className="col-check">
                                    <input type="checkbox" checked={allSelected} onChange={handleSelectAll} aria-label="Select all" />
                                </th>
                                <th>Date</th>
                                <th>Party Name</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredSheets.length === 0 ? (
                                <tr className="list-empty-row">
                                    <td colSpan="4">
                                        <div className="list-empty-state">
                                            <h3>No measure sheets found</h3>
                                            <p>{searchTerm ? 'Try a different search term.' : 'Create your first measure sheet to get started.'}</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredSheets.map(sheet => (
                                <tr
                                    key={sheet.id}
                                    className={`list-row${selectedSheets.includes(sheet.id) ? ' is-selected' : ''}`}
                                    onClick={(e) => {
                                        if (e.target.type !== 'checkbox' && e.target.type !== 'button') {
                                            navigate(`/createMeasureSheet/${sheet.id}`);
                                        }
                                    }}
                                >
                                    <td className="col-check" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={selectedSheets.includes(sheet.id)}
                                            onChange={() => handleCheckboxChange(sheet.id)}
                                        />
                                    </td>
                                    <td>{formatDateDDMMYYYY(sheet.sheetDate)}</td>
                                    <td><span className="list-primary-text">{sheet.partyName}</span></td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <button type="button" className="list-secondary-button" onClick={() => handleDuplicate(sheet)}>
                                            Duplicate
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        </PageLoadingArea>
    );
}

export default MeasureSheetList;
